import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { getTimezone, setTimezone } from "../../../timezone/api";

export const Route = createFileRoute("/_workspace/settings/timezone")({
  component: TimezonePane,
});

/** The browser's best guess at the current user's IANA timezone. */
function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/** Every IANA timezone the runtime knows, with a defensive fallback. */
function supportedTimeZones(): string[] {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["UTC"];
  }
}

/** Human-friendly label for an IANA zone id ("America/New_York" → "America/New York"). */
function zoneLabel(zone: string): string {
  return zone.replace(/_/g, " ");
}

/**
 * Timezone options with the current user's zone surfaced first, then the
 * `saved` value (so the trigger can always display it), then the rest in the
 * runtime's order — deduped.
 */
function useZoneOptions(saved: string | undefined): string[] {
  return useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    const push = (z: string | undefined) => {
      if (z && !seen.has(z)) {
        seen.add(z);
        ordered.push(z);
      }
    };
    push(browserTimeZone());
    push(saved);
    for (const z of supportedTimeZones()) push(z);
    return ordered;
  }, [saved]);
}

function TimezonePane() {
  const qc = useQueryClient();
  const current = useQuery({
    queryKey: ["timezone"],
    queryFn: getTimezone,
  });

  // Local override of the saved value; until the user picks something, the
  // combobox reflects whatever is saved.
  const [selected, setSelected] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const value = selected ?? current.data;

  const options = useZoneOptions(current.data);

  const save = useMutation({
    mutationFn: (tz: string) => setTimezone(tz),
    onSuccess: (saved) => {
      qc.setQueryData(["timezone"], saved);
      setSelected(null);
    },
  });

  const dirty = value !== undefined && value !== current.data;
  const device = browserTimeZone();
  const deviceDiffers = !!value && device !== value;

  return (
    <div className="mx-auto w-full max-w-2xl px-6 pt-8 pb-12">
      <h1 className="text-lg font-semibold">Timezone</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        The timezone for this household. Default is the timezone of the first
        person to sign in.
      </p>

      <section className="mt-6">
        {current.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : current.error ? (
          <p className="text-sm text-destructive">
            {(current.error as Error).message}
          </p>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (dirty && value) save.mutate(value);
            }}
          >
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  aria-label="Timezone"
                  disabled={save.isPending}
                  className="w-full max-w-sm justify-between font-normal"
                >
                  <span className="truncate">
                    {value ? zoneLabel(value) : "Select a timezone"}
                  </span>
                  <ChevronsUpDown className="ml-2 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                <Command>
                  <CommandInput placeholder="Search timezones…" />
                  <CommandList>
                    <CommandEmpty>No timezone found.</CommandEmpty>
                    <CommandGroup>
                      {options.map((z) => (
                        <CommandItem
                          key={z}
                          value={z}
                          onSelect={() => {
                            setSelected(z);
                            setOpen(false);
                          }}
                        >
                          {zoneLabel(z)}
                          <Check
                            className={cn(
                              "ml-auto",
                              value === z ? "opacity-100" : "opacity-0",
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {deviceDiffers ? (
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => setSelected(device)}
                className="h-auto w-fit p-0 text-xs text-muted-foreground hover:text-foreground"
              >
                Use this device's timezone ({zoneLabel(device)})
              </Button>
            ) : null}

            {save.error ? (
              <p className="text-sm text-destructive">
                {(save.error as Error).message}
              </p>
            ) : null}

            <div>
              <Button type="submit" disabled={!dirty || save.isPending}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
